import React from 'react';
import useGameStore from '../../store/gameStore';
import { computeReseauEffects } from '../../utils/magasin';
import { computeAmelFx, bilanAmeliorations } from '../../utils/ameliorations';
import { getLocalisation, getTypeMagasin } from '../../utils/ouverture';
import { formatMoney } from '../../utils/format';
import { BarChart3 } from 'lucide-react';

/* SCD R3 — Comparateur des points de vente : CA estimé, équipements, satisfaction. */
export const ComparateurMagasins = () => {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays) || 0;
  if (!retail?.magasin) return null;

  const reseau = computeReseauEffects(retail, totalDays);
  const caJour = retail.stats?.histo?.length ? retail.stats.histo[retail.stats.histo.length - 1].ca : 0;
  const demandeMult = Math.max(1, reseau.demandeMult || 1);
  const bilan = bilanAmeliorations(retail);
  const satGlobal = Math.round(retail.marketing?.satisfaction ?? 0);

  const lignes = [
    {
      mag: retail.magasin, principal: true,
      nom: retail.magasin.nom || retail.magasin.nomType || 'Magasin principal',
      caEstime: caJour / demandeMult,
      bilanMag: bilan.parMagasin[0],
    },
    ...(retail.magasins || []).map((m, i) => ({
      mag: m, principal: false,
      nom: m.nom || m.nomType || `Magasin ${i + 2}`,
      caEstime: caJour * ((reseau.detail?.[i]?.contribution || 0) / demandeMult),
      bilanMag: bilan.parMagasin[i + 1],
    })),
  ];

  return (
    <div className="surface-card p-4" data-testid="comparateur-magasins">
      <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-1 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-accent" /> Comparateur des points de vente
      </h3>
      <p className="text-[11px] text-slate-500 mb-3">CA estimé à partir du dernier jour et de la contribution de chaque magasin. La satisfaction est celle de l'enseigne, améliorée par les équipements de chaque point de vente.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-ink-600">
              <th className="py-2 pr-3">Magasin</th>
              <th className="py-2 pr-3">Type · Localisation</th>
              <th className="py-2 pr-3">CA estimé / jour</th>
              <th className="py-2 pr-3">Équipements</th>
              <th className="py-2 pr-3">Effets améliorations</th>
              <th className="py-2">Satisfaction</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {lignes.map((l, i) => {
              const type = getTypeMagasin(l.mag.typeId);
              const loc = getLocalisation(l.mag.localisationId);
              const fx = computeAmelFx(l.mag);
              const satPts = Math.round(fx.satisfactionJour / 0.02);
              return (
                <tr key={l.mag.id || 'principal'} data-testid={`comparateur-ligne-${i}`}>
                  <td className="py-2 pr-3 font-bold text-slate-100">
                    {type?.emoji || '🏪'} {l.nom}
                    {l.principal && <span className="ml-1.5 text-[9px] uppercase text-accent">principal</span>}
                  </td>
                  <td className="py-2 pr-3 text-slate-400">{type?.nom || '—'} · {loc?.nom || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-slate-200" data-testid={`comparateur-ca-${i}`}>{formatMoney(Math.round(l.caEstime))}</td>
                  <td className="py-2 pr-3 text-slate-300">
                    {l.bilanMag?.nb || 0} équip. · <span className="text-slate-500">entretien {formatMoney(l.bilanMag?.entretienMois || 0)}/mois</span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-emerald-400">
                    {fx.traficPct + fx.conversionPct + fx.panierPct > 0
                      ? `+${fx.traficPct + fx.conversionPct + fx.panierPct} % (trafic·conv·panier)`
                      : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="py-2 font-mono text-slate-200">
                    {satGlobal}/100{satPts > 0 && <span className="text-emerald-400"> (+{satPts} pts équip.)</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
